import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore, db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, orderBy, deleteDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { useCollection } from 'react-firebase-hooks/firestore';
import { Link } from 'react-router-dom';
import './myprofile.css';

const ProfilePage = () => {
  const [user] = useAuthState(auth);
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    province: '',
    joinDate: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');

  // Query for user's ads
  const adsRef = collection(db, 'ads');
  const adsQuery = user ? query(adsRef, where('userId', '==', user.uid), orderBy('createdAt', 'desc')) : null;
  const [adsSnapshot, adsLoading, adsError] = useCollection(adsQuery);

  const provinces = [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
  ];

  const fetchProfile = useCallback(async () => {
    if (user) {
      console.log('Fetching profile for user:', user.uid);
      const userRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data fetched:', userData);
        setProfile({
          displayName: user.displayName || '',
          email: user.email || '',
          phoneNumber: userData.phoneNumber || '',
          province: userData.province || '',
          joinDate: userData.joinDate?.toDate() || null,
        });
      } else {
        console.log('User document does not exist, creating new one');
        await setDoc(userRef, {
          joinDate: new Date(),
          email: user.email,
        });
        setProfile(prevProfile => ({
          ...prevProfile,
          email: user.email,
          joinDate: new Date(),
        }));
      }
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prevProfile => ({ ...prevProfile, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit called');
    try {
      console.log('Submitting profile update');
      console.log('Current user:', user);
      console.log('Updating document:', user.uid);
      console.log('Update data:', {
        displayName: profile.displayName,
        phoneNumber: profile.phoneNumber,
        province: profile.province,
      });

      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        displayName: profile.displayName,
        phoneNumber: profile.phoneNumber,
        province: profile.province,
      });

      await updateProfile(user, { displayName: profile.displayName });

      setMessage('Profile updated successfully!');
      setIsEditing(false);
      console.log('Profile update completed');
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile. Please try again.');
    }
  };

  const handleEdit = (e) => {
    e.preventDefault(); // Prevent any default action
    console.log('Entering edit mode');
    setIsEditing(true);
    setMessage('');
  };

  const handleCancel = (e) => {
    e.preventDefault(); // Prevent any default action
    console.log('Cancelling edit mode');
    setIsEditing(false);
    setMessage('');
    fetchProfile(); // Reset to original values
  };

  const handleDeleteAd = async (adId) => {
    if (!user) return;

    if (window.confirm('Are you sure you want to delete this ad?')) {
      try {
        await deleteDoc(doc(db, 'ads', adId));
        setMessage('Ad deleted successfully!');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting ad: ', error);
        setMessage('Failed to delete ad. Please try again.');
      }
    }
  };

  if (!user) {
    return (
      <Container className="profile-container">
        <Alert variant="warning" className="profile-alert">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  console.log('Rendering ProfilePage, isEditing:', isEditing);

  // Prepare ads data
  const userAds = adsSnapshot?.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [];

  return (
    <Container className="profile-container">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="profile-card">
            <Card.Header as="h2" className="text-center profile-card-header">My Profile</Card.Header>
            <Card.Body className="profile-card-body">
              {message && <Alert variant="info" className="profile-alert">{message}</Alert>}
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Name</Form.Label>
                  <Form.Control
                    className="profile-form-control"
                    type="text"
                    name="displayName"
                    value={profile.displayName}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Email</Form.Label>
                  <Form.Control
                    className="profile-form-control"
                    type="email"
                    value={profile.email}
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Phone Number</Form.Label>
                  <Form.Control
                    className="profile-form-control"
                    type="tel"
                    name="phoneNumber"
                    value={profile.phoneNumber}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="profile-form-label">Province</Form.Label>
                  <Form.Select
                    className="profile-form-select"
                    name="province"
                    value={profile.province}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  >
                    <option value="">Select a province</option>
                    {provinces.map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {profile.joinDate && (
                  <Form.Group className="mb-3">
                    <Form.Label className="profile-form-label">Join Date</Form.Label>
                    <Form.Control
                      className="profile-form-control profile-join-date"
                      type="text"
                      value={profile.joinDate.toLocaleDateString()}
                      disabled
                    />
                  </Form.Group>
                )}
                <div className="d-grid gap-2">
                  {isEditing ? (
                    <>
                      <Button className="profile-btn profile-btn-primary" type="submit">Save Changes</Button>
                      <Button className="profile-btn profile-btn-secondary" onClick={handleCancel}>Cancel</Button>
                    </>
                  ) : (
                    <Button className="profile-btn profile-btn-primary" onClick={handleEdit}>Edit Profile</Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* My Listings Section */}
      <Row className="mt-4">
        <Col>
          <Card className="listings-card">
            <Card.Header as="h3" className="profile-card-header">
              My Listings
              {!adsLoading && userAds.length > 0 && 
                <Badge bg="secondary" className="ms-2">{userAds.length}</Badge>
              }
            </Card.Header>
            <Card.Body>
              {adsLoading ? (
                <div className="text-center">Loading your listings...</div>
              ) : adsError ? (
                <Alert variant="danger">Error loading listings: {adsError.message}</Alert>
              ) : userAds.length === 0 ? (
                <Alert variant="info">You have no listings yet.</Alert>
              ) : (
                <Row>
                  {userAds.map(ad => (
                    <Col key={ad.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                      <Card className="listing-card">
                        <div style={{ position: 'relative' }}>
                          <Card.Img 
                            variant="top" 
                            src={ad.imageUrls ? ad.imageUrls[0] : ad.imageUrl || 'https://via.placeholder.com/150'}
                            className="listing-card-img"
                          />
                          {ad.imageUrls && ad.imageUrls.length > 1 && (
                            <Badge bg="secondary" className="listing-badge">
                              {ad.imageUrls.length}
                            </Badge>
                          )}
                        </div>
                        <Card.Body>
                          <Card.Title>{ad.ItemName || 'Untitled Ad'}</Card.Title>
                          <Card.Text>
                            R{ad.Price ? ad.Price.toLocaleString() : 'Not specified'}
                          </Card.Text>
                          <div className="listing-card-actions">
                            <Button 
                              as={Link} 
                              to={`/view-ad/${ad.id}`}
                              variant="outline-primary" 
                              size="sm">
                              View
                            </Button>
                            <Button 
                              as={Link} 
                              to={`/edit-ad/${ad.id}`} 
                              variant="outline-secondary" 
                              size="sm">
                              Edit
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDeleteAd(ad.id)}>
                              Delete
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;